import React, { useEffect, useMemo, useState } from 'react';
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import CircularProgress from "@mui/joy/CircularProgress";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Select from "@mui/joy/Select";
import Option from "@mui/joy/Option";
import Input from "@mui/joy/Input";
import Button from "@mui/joy/Button";
import { LineChart } from "@mui/x-charts/LineChart";
import { BalanceApi, BalanceGroup } from './balanceApi';
import { BalanceHistoryItemDto } from '../../../../models/balance';
import {NotificationService} from "../../../../common/notification";
import {kopecksToRublesNumber} from "../../../../common/money";

export function BalancePage() {
    const toInputValue = (date: Date) => {
        const tzOffsetMs = date.getTimezoneOffset() * 60000;
        const local = new Date(date.getTime() - tzOffsetMs);
        return local.toISOString().slice(0, 16);
    };

    const now = new Date();
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState<string>(toInputValue(oneMonthAgo));
    const [toDate, setToDate] = useState<string>(toInputValue(now));
    const [group, setGroup] = useState<BalanceGroup>(BalanceGroup.Day);
    const [data, setData] = useState<BalanceHistoryItemDto[] | null>(null);

    const [isSmallScreen, setIsSmallScreen] = useState<boolean>(false);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 600);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const request = {
                fromUtc: fromDate ? new Date(fromDate).toISOString() : new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString(),
                toUtc: toDate ? new Date(toDate).toISOString() : new Date().toISOString(),
                group,
            };
            const response = await BalanceApi.getHistory(request);
            if(!response.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки")
                return;
            }
            setData(response.value!);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData().catch(console.error);
    }, []);

    const dataset = useMemo(() => {
        if (!data) return [] as any[];
        return data
            .slice()
            .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
            .map(it => ({
                x: new Date(it.date),
                site: kopecksToRublesNumber(it.siteBalance),
                tome: kopecksToRublesNumber(it.tomeBalance),
                gmoney: kopecksToRublesNumber(it.gameMoneyBalance),
                net: kopecksToRublesNumber(it.tomeBalance + it.gameMoneyBalance-it.siteBalance),
            }));
    }, [data]);

    const pad2 = (num: number) => num.toString().padStart(2, '0');

    const isSingleYear = useMemo(() => {
        if (!data || data.length === 0) return false;
        const years = new Set<number>();
        for (const it of data) {
            years.add(new Date(it.date).getFullYear());
            if (years.size > 1) return false;
        }
        return true;
    }, [data]);

    const xFormatter = useMemo(() => {
        return (v: Date) => {
            const day = pad2(v.getDate());
            const month = pad2(v.getMonth() + 1);
            const year = v.getFullYear();
            if (group === BalanceGroup.Hour) {
                const hours = pad2(v.getHours());
                const minutes = pad2(v.getMinutes());
                return `${day}.${month}${isSingleYear ? '' : `.${year}`} ${hours}:${minutes}`;
            }
            return `${day}.${month}${isSingleYear ? '' : `.${year}`}`;
        };
    }, [group, isSingleYear]);

    const allSeries = [
        { dataKey: 'site', label: 'Баланс сайта', color: '#1976d2', connectNulls: true, curve: 'monotoneX', showMark: !isSmallScreen, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—', strokeWidth: isSmallScreen ? 1.5 : 2.25 },
        { dataKey: 'tome', label: 'Баланс Tome', color: '#2e7d32', connectNulls: true, curve: 'monotoneX', showMark: !isSmallScreen, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—', strokeWidth: isSmallScreen ? 1.5 : 2.25 },
        { dataKey: 'gmoney', label: 'Баланс GMoney', color: '#6a1b9a', connectNulls: true, curve: 'monotoneX', showMark: !isSmallScreen, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—', strokeWidth: isSmallScreen ? 1.5 : 2.25 },
        { dataKey: 'net', label: 'Чистая позиция по ликвидности', color: '#d32f2f', connectNulls: true, curve: 'monotoneX', showMark: !isSmallScreen, valueFormatter: (v: number | null) => v != null ? `${v.toLocaleString('ru-RU')} ₽` : '—', strokeWidth: isSmallScreen ? 1.5 : 2.25 },
    ];

    const minScrollableWidth = useMemo(() => {
        if (!isSmallScreen) return undefined as number | undefined;
        const points = dataset.length || 0;
        const perPoint = 48; // px per point on mobile
        return Math.max(720, points * perPoint);
    }, [isSmallScreen, dataset.length]);

    return (
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography level="h4" sx={{ mb: 2 }}>
                Баланс
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', p: 2, bgcolor: 'background.level1', borderRadius: '8px', mb: 3 }}>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>От даты</FormLabel>
                    <Input type="datetime-local" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 150 }}>
                    <FormLabel>До даты</FormLabel>
                    <Input type="datetime-local" value={toDate} onChange={(e) => setToDate(e.target.value)} />
                </FormControl>
                <FormControl size="sm" sx={{ minWidth: 120 }}>
                    <FormLabel>Группировка</FormLabel>
                    <Select value={group} onChange={(_, newValue) => setGroup(newValue as BalanceGroup)}>
                        <Option value={BalanceGroup.Hour}>По часам</Option>
                        <Option value={BalanceGroup.Day}>По дням</Option>
                        <Option value={BalanceGroup.Month}>По месяцам</Option>
                        <Option value={BalanceGroup.Year}>По годам</Option>
                    </Select>
                </FormControl>
                <Button onClick={loadData} loading={loading} sx={{ alignSelf: 'end' }}>Обновить</Button>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <Box sx={{ mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                        Баланс сайта и платежных систем (руб.)
                    </Typography>
                    <Box sx={{ p: 2, bgcolor: 'background.level1', borderRadius: '8px', border: '1px solid', borderColor: 'divider', overflowX: { xs: 'auto', md: 'visible' } }}>
                        <Box sx={{ minWidth: { xs: minScrollableWidth ?? 0, sm: 0 } }}>
                            <LineChart
                                dataset={dataset}
                                xAxis={[{ dataKey: 'x', scaleType: 'time', valueFormatter: xFormatter }]}
                                series={allSeries as any}
                                height={isSmallScreen ? 280 : 420}
                            />
                        </Box>
                    </Box>
                </Box>
            )}
            <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 1 }}>
                Чистая позиция по ликвидности = (Баланс Tome + Баланс GMoney) - Баланс сайта.
            </Typography>
        </Box>
    );
}


